import React, { Component } from "react";
import { toast } from "react-toastify";
import { downloadDemandFileService } from "../../../services/workflow.service";

const formatDetailDate = (value) => {
  if (!value || value === "0001-01-01T00:00:00") return "-";
  return new Date(value).toLocaleString("tr-TR");
};

const getDemandDocuments = (demand) => {
  const documents = demand?.dokumanlar || demand?.Dokumanlar || [];
  return Array.isArray(documents) ? documents.filter((item) => item) : [];
};

const getDocumentName = (document) =>
  document?.adi || document?.Adi || "-";

const getFileExtension = (fileName) => {
  if (!fileName || !fileName.includes(".")) {
    return "file";
  }

  return fileName.split(".").pop().toLowerCase();
};

const getDocumentVisuals = (fileName) => {
  const extension = getFileExtension(fileName);

  if (extension === "pdf") {
    return {
      iconClass: "t-icon-file-pdf",
      typeClass: "pdf",
      label: "PDF Dosyası",
    };
  }

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
    return {
      iconClass: "t-icon-file-picture",
      typeClass: "image",
      label: "Görsel Dosya",
    };
  }

  if (["doc", "docx"].includes(extension)) {
    return {
      iconClass: "t-icon-file-word",
      typeClass: "word",
      label: "Word Dosyası",
    };
  }

  if (["xls", "xlsx"].includes(extension)) {
    return {
      iconClass: "t-icon-file-excel",
      typeClass: "excel",
      label: "Excel Dosyası",
    };
  }

  return {
    iconClass: "t-icon-file-text",
    typeClass: "default",
    label: extension === "file" ? "Dosya" : `${extension.toUpperCase()} Dosyası`,
  };
};

const getDemandValue = (demand, ...keys) => {
  for (const key of keys) {
    const value = demand?.[key];
    if (value != null && value !== "") {
      return value;
    }
  }
  return "";
};

export default class DemandDetail extends Component {
  state = {
    downloadingDocumentId: null,
  };

  componentDidMount() {
    document.addEventListener("keydown", this.handleKeyDown);
  }

  componentWillUnmount() {
    document.removeEventListener("keydown", this.handleKeyDown);
  }

  handleKeyDown = (event) => {
    if (event.key === "Escape") {
      this.props.onClose();
    }
  };

  handleDownloadDocument = (document) => {
    const documentId = document?.id ?? document?.Id;
    const documentName = getDocumentName(document);

    if (!documentId) {
      toast.error("Doküman indirilemedi");
      return;
    }

    this.setState({ downloadingDocumentId: documentId });
    downloadDemandFileService(documentId, documentName)
      .catch(() => {
        toast.error("Doküman indirilemedi");
      })
      .finally(() => {
        this.setState({ downloadingDocumentId: null });
      });
  };

  renderField = (label, value, options = {}) => (
    <div className={`t-memberInfo-field${options.full ? " t-demandDetailModal-fieldFull" : ""}`}>
      <span className="t-memberInfo-fieldLabel">{label}</span>
      <span
        className={`t-memberInfo-fieldValue${
          options.multiline ? " t-demandDetailModal-valueMultiline" : ""
        }`}
      >
        {value || "-"}
      </span>
    </div>
  );

  renderDocuments = () => {
    const documents = getDemandDocuments(this.props.demand);
    const { downloadingDocumentId } = this.state;

    if (documents.length === 0) {
      return null;
    }

    return (
      <div className="t-demandDetailModal-documents t-demandDetailModal-fieldFull">
        <div className="t-demandDetailModal-documentsHeader">
          <div>
            <span className="t-demandDetailModal-documentsTitle">Dokümanlar</span>
            <p className="t-demandDetailModal-documentsSubtitle">
              Talebe eklenen dosyaları görüntüleyebilir veya indirebilirsiniz.
            </p>
          </div>
          <span className="t-demandDetailModal-documentsCount">{documents.length}</span>
        </div>

        <ul className="t-demandDetailModal-documentList">
          {documents.map((document, index) => {
            const documentId = document?.id ?? document?.Id ?? index;
            const documentName = getDocumentName(document);
            const isDownloading = downloadingDocumentId === documentId;
            const visuals = getDocumentVisuals(documentName);

            return (
              <li className="t-demandDetailModal-documentItem" key={documentId}>
                <div
                  className={`t-demandDetailModal-documentIcon t-demandDetailModal-documentIcon--${visuals.typeClass}`}
                >
                  <i className={visuals.iconClass} aria-hidden="true" />
                </div>

                <div className="t-demandDetailModal-documentInfo">
                  <span className="t-demandDetailModal-documentName" title={documentName}>
                    {documentName}
                  </span>
                  <span className="t-demandDetailModal-documentMeta">{visuals.label}</span>
                </div>

                <button
                  type="button"
                  className={`t-demandDetailModal-documentDownload${
                    isDownloading ? " is-loading" : ""
                  }`}
                  disabled={isDownloading}
                  onClick={() => this.handleDownloadDocument(document)}
                >
                  {isDownloading ? (
                    <>
                      <span className="t-demandDetailModal-documentSpinner" aria-hidden="true" />
                      <span>İndiriliyor</span>
                    </>
                  ) : (
                    <>
                      <i className="t-icon-folder-download" aria-hidden="true" />
                      <span>İndir</span>
                    </>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  render() {
    const { demand, onClose } = this.props;
    const hasResponse = demand?.musteriCevap != null && demand?.musteriCevap !== "";
    const yanlisUyeNo = getDemandValue(demand, "yanlisUyeNo", "YanlisUyeNo");
    const dogruUyeNo = getDemandValue(demand, "dogruUyeNo", "DogruUyeNo");
    const dogruUyeNoLabel = dogruUyeNo && !yanlisUyeNo ? "Üye No" : "Doğru Üye No";

    return (
      <>
        <div
          className="modal fade show t-demandDetailModal"
          tabIndex="-1"
          role="dialog"
          aria-labelledby={`demandDetailTitle${demand.talepNo}`}
          aria-modal="true"
          style={{ display: "block", zIndex: 1055 }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content t-guarantorModal-content border-0">
              <div className="t-guarantorModal-header">
                <div className="t-guarantorModal-header-content">
                  <div className="t-guarantorModal-icon">
                    <i className="t-icon-search1" aria-hidden="true" />
                  </div>
                  <div>
                    <h5 className="t-guarantorModal-title" id={`demandDetailTitle${demand.talepNo}`}>
                      Talep Detayı
                    </h5>
                    <p className="t-guarantorModal-subtitle">
                      Talep No: <strong>{demand?.talepNo || "-"}</strong>
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  className="t-guarantorModal-close"
                  onClick={onClose}
                  aria-label="Kapat"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
              </div>

              <div className="t-guarantorModal-body t-demandDetailModal-body">
                <div className="t-memberInfo-grid t-demandDetailModal-grid">
                  {this.renderField("Talep Durumu", demand?.durumu)}
                  {this.renderField(
                    "Başvuru Nedeni",
                    demand?.basvuruNedeni || demand?.BasvuruNedeni || demand?.islem
                  )}
                  {this.renderField("Açılma Tarihi", formatDetailDate(demand?.acilisTarihi))}
                  {this.renderField("Kapanma Tarihi", formatDetailDate(demand?.kapanisTarihi))}
                  {yanlisUyeNo && this.renderField("Yanlış Üye No", yanlisUyeNo)}
                  {dogruUyeNo && this.renderField(dogruUyeNoLabel, dogruUyeNo)}
                  {this.renderField("Konu", demand?.konu, { full: true })}
                  {this.renderField("Açıklama", demand?.aciklama, { full: true, multiline: true })}
                  {hasResponse &&
                    this.renderField("Cevap", demand?.musteriCevap, {
                      full: true,
                      multiline: true,
                    })}
                  {this.renderDocuments()}
                </div>
              </div>

              <div className="t-guarantorModal-footer">
                <button
                  type="button"
                  className="t-memberInfo-btn t-memberInfo-btn--secondary"
                  onClick={onClose}
                >
                  <span>Kapat</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div
          className="modal-backdrop fade show t-demandDetailModal-backdrop"
          style={{ zIndex: 1050 }}
          onClick={onClose}
          aria-hidden="true"
        />
      </>
    );
  }
}
